import React from 'react';
import { AtonTargetInfo } from '@ais-ui/websockets';
import { useLocalization } from '@ais-ui/locale-management';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { isNonEmpty } from '@ais-ui/utils';
import { Separator, TruncatedText } from '@ais-ui/ui-core';
import { useLengthUnits } from '../Units/useLengthUnits';

interface IdentificationItemProps {
  label: string;
  value: string;
}

const IdentificationItem = ({ label, value }: IdentificationItemProps) => {
  return (
    <div className={'flex flex-col mt-1 min-w-0'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <TruncatedText
        text={isNonEmpty(value) ? value : 'Unknown'}
        className="text-xs select-text"
        maxWidth="100%"
      />
    </div>
  );
};

interface IdentificationProps {
  atonInfo: AtonTargetInfo;
}

const Identification = ({ atonInfo }: IdentificationProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <IdentificationItem
        label={messages.shipDetailsBaseNameLabel}
        value={atonInfo.name}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseMMSILabel}
        value={atonInfo.mmsi}
      />
      <IdentificationItem
        label={messages.shipDetailsBaseCountryLabel}
        value={atonInfo.country}
      />
      <IdentificationItem
        label={messages.reportsDetailsTypeLabel}
        value={atonInfo.targetType}
      />
      <IdentificationItem
        label={messages.targetDetailsAtonTypeLabel}
        value={atonInfo.atonType}
      />
    </div>
  );
};

interface PhysicalCharacteristicsProps {
  atonInfo: AtonTargetInfo;
}

interface PhysicalCharacteristicsItemProps {
  label: string;
  value: string;
}

const PhysicalCharacteristicsItem = ({
  label,
  value,
}: PhysicalCharacteristicsItemProps) => {
  const { convertLengthString } = useLengthUnits();
  const convertedValue = convertLengthString(value);

  return (
    <div className={'flex flex-col mt-1 min-w-0'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <TruncatedText
        text={isNonEmpty(convertedValue) ? convertedValue : '-'}
        className="text-xs select-text"
        maxWidth="100%"
      />
    </div>
  );
};

const PhysicalCharacteristics = ({
  atonInfo,
}: PhysicalCharacteristicsProps) => {
  const { messages } = useLocalization();

  return (
    <div className="grid grid-cols-2 gap-1">
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseWidthLabel}
        value={atonInfo.width}
      />
      <PhysicalCharacteristicsItem
        label={messages.shipDetailsBaseLengthLabel}
        value={atonInfo.length}
      />
    </div>
  );
};

interface OperationalStatusProps {
  atonInfo: AtonTargetInfo;
}

interface OperationalStatusItemProps {
  label: string;
  value: string;
}

const OperationalStatusItem = ({
  label,
  value,
}: OperationalStatusItemProps) => {
  return (
    <div className={'flex flex-col mt-1 min-w-0'}>
      <span className="text-muted-foreground text-xs">{label}</span>
      <TruncatedText
        text={isNonEmpty(value) ? value : 'Unknown'}
        className="text-xs select-text"
        maxWidth="100%"
      />
    </div>
  );
};

const OperationalStatus = ({ atonInfo }: OperationalStatusProps) => {
  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <div className="grid grid-cols-2 gap-1">
      <OperationalStatusItem
        label={messages.shipDetailsBaseLatLabel}
        value={atonInfo.latitudeDegrees}
      />
      <OperationalStatusItem
        label={messages.shipDetailsBaseLonLabel}
        value={atonInfo.longitudeDegrees}
      />
      <OperationalStatusItem
        label={messages.shipDetailsBaseLastAISReportLabel}
        value={formatDateTimeWithTimeZone(atonInfo.lastAisReport)}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisLatitudeLabel}
        value={atonInfo.lastAisLatitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisLongitudeLabel}
        value={atonInfo.lastAisLongitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisSatLatitudeLabel}
        value={atonInfo.lastAisSatLatitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisSatLongitudeLabel}
        value={atonInfo.lastAisSatLongitude}
      />
      <OperationalStatusItem
        label={messages.targetDetailsLastAisSatReportLabel}
        value={formatDateTimeWithTimeZone(atonInfo.lastAisSatReport)}
      />
      <OperationalStatusItem
        label={messages.shipDetailsStateLabel}
        value={atonInfo.state}
      />
      <OperationalStatusItem
        label={messages.shipDetailsAtonAssignedModeLabel}
        value={atonInfo.assignedMode}
      />
      <OperationalStatusItem
        label={messages.shipDetailsAtonOffPositionLabel}
        value={atonInfo.offPosition}
      />
      {atonInfo?.analogInternal && (
        <OperationalStatusItem
          label={messages.shipDetailsAtonAnalogInternalLabel}
          value={atonInfo.analogInternal}
        />
      )}

      {atonInfo?.analogInternal && (
        <OperationalStatusItem
          label={messages.shipDetailsAtonAnalogExternal1Label}
          value={atonInfo.analogExternal1}
        />
      )}

      {atonInfo?.analogInternal && (
        <OperationalStatusItem
          label={messages.shipDetailsAtonAnalogExternal2Label}
          value={atonInfo.analogExternal2}
        />
      )}

      {atonInfo?.analogInternal && (
        <OperationalStatusItem
          label={messages.shipDetailsAtonStatusInternalLabel}
          value={atonInfo.statusInternal}
        />
      )}

      {atonInfo?.analogInternal && (
        <OperationalStatusItem
          label={messages.shipDetailsAtonStatusExternalLabel}
          value={atonInfo.statusExternal}
        />
      )}
    </div>
  );
};

export const AtonDetailsPanel = ({
  atonInfo,
}: {
  atonInfo: AtonTargetInfo;
}) => {
  return (
    <div className="grid gap-6">
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">
            Identification and Basic Details
          </h3>
          <Separator className="mt-2"></Separator>
        </div>
        <Identification atonInfo={atonInfo}></Identification>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Physical Characteristics</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <PhysicalCharacteristics atonInfo={atonInfo}></PhysicalCharacteristics>
      </div>
      <div className="grid gap-1">
        <div>
          <h3 className="text-base font-medium">Operational Status</h3>
          <Separator className="mt-2"></Separator>
        </div>
        <OperationalStatus atonInfo={atonInfo}></OperationalStatus>
      </div>
    </div>
  );
};
